import * as React from 'react';
import Zoom from '@mui/material/Zoom';
import RtspPlayer from 'components/RtspPlayer';
import { getWebsocketHost, settingsConfig } from 'utils/commonFunc';
import styles from '../css/gis.module.css';

const VIDEO_SLOTS = [
    { key: 'north', direction: 'N', className: 'accessCctvNorth' },
    { key: 'east', direction: 'E', className: 'accessCctvEast' },
    { key: 'south', direction: 'S', className: 'accessCctvSouth' },
    { key: 'west', direction: 'W', className: 'accessCctvWest' },
    { key: 'center', direction: 'C', className: 'accessCctvCenter' },
];

const ACCESS_CCTV_PHOTO_FALLBACK = '/images/facility/cctv_thumbnail.jpg';

// 접근 지점 ID를 기준으로 현장 사진 URL을 생성합니다.
function createAccessPhotoUrl(accessPointId) {
    if (!accessPointId) {
        return ACCESS_CCTV_PHOTO_FALLBACK;
    }

    return `/images/access-cctv/${String(accessPointId).toLowerCase()}.jpg`;
}

// 접근로 현장 사진을 불러오지 못하면 기존 공통 이미지로 대체합니다.
function handleAccessPhotoError(event) {
    event.currentTarget.onerror = null;
    event.currentTarget.src = ACCESS_CCTV_PHOTO_FALLBACK;
}

// 영상 중계 서버에 전달할 WebSocket 재생 주소를 생성합니다.
function createStreamUrl(streamId, serviceConfig) {
    if (!streamId) {
        return undefined;
    }

    const params = new URLSearchParams({
        id: String(streamId),
        width: String(serviceConfig.cctvWidth),
        height: String(serviceConfig.cctvHeight),
        fps: String(serviceConfig.cctvFps),
    });

    return `${getWebsocketHost('/websocket/stream', serviceConfig.wsPortCctv)}?${params.toString()}`;
}

// DB 중앙 영상과 백엔드 설정의 방향별 영상을 접근로 형태로 표시합니다.
export const PopupViewAccessCctv = ({ item }) => {
    const [transitionIn, setTransitionIn] = React.useState(false);

    React.useEffect(() => {
        setTransitionIn(false);
        const key = setTimeout(() => setTransitionIn(true), 100);
        return () => clearTimeout(key);
    }, [item.id]);

    const serviceConfig = settingsConfig();
    const accessLocationName = item.accessName ?? item.nm ?? '접근로';
    const accessPhotoUrl = createAccessPhotoUrl(item.accessConfig?.accessPointId);
    const videoItems = VIDEO_SLOTS.map(slot => {
        const camera = slot.direction === 'C'
            ? {
                streamId: item.id,
                locationText: item.accessName ?? item.nm,
            }
            : item.accessConfig?.cameras?.find(
                accessCamera => accessCamera.direction === slot.direction
            );

        return {
            ...slot,
            ...camera,
            url: createStreamUrl(camera?.streamId, serviceConfig),
        };
    });

    return (
        <Zoom in={transitionIn} timeout={500} easing={{ enter: 'ease-out', exit: 'ease-in' }}>
            <div className={`${styles.gisPopup_accessCctv} gisPopup flx flx_col gap6`}>
                <div className={`${styles.accessCctvHeader} flx a_center`}>
                    <div className="popTit">CCTV </div>
                    <span className="id">{item.accessName ?? item.nm}</span>
                    <span className={styles.accessCctvHeaderId}>
                        ( ID : {item.accessId ?? item.id} )
                    </span>
                </div>

                <div className={styles.accessCctvGrid}>
                    <div className={`${styles.accessCctvCell} ${styles.accessCctvTopLeft}`} />

                    {videoItems.map(video => (
                        <div
                            key={video.key}
                            className={`${styles.accessCctvCell} ${styles.accessCctvVideo} ${styles[video.className]}`}
                        >
                            <div className={styles.accessCctvStream}>
                                {video.url ? (
                                    <div className={styles.accessCctvPlayer}>
                                        <RtspPlayer
                                            url={video.url}
                                            fps={serviceConfig.cctvFps}
                                        />
                                    </div>
                                ) : (
                                    <div className={styles.accessCctvVideoEmpty}>
                                        영상 미설정
                                    </div>
                                )}

                                {video.direction !== 'C' && video.locationText && (
                                    <div className={styles.accessCctvLocationText}>
                                        {video.locationText}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}

                    <div className={`${styles.accessCctvCell} ${styles.accessCctvTopRight}`} />

                    <div className={`${styles.accessCctvCell} ${styles.accessCctvBottomLeft} ${styles.accessCctvStatus}`}>
                        {videoItems.map(video => {
                            const isCenter = video.direction === 'C';
                            const isNormal = isCenter
                                ? Number(item.stat) === 1
                                : Number(video.stat) === 1;

                            return (
                                <div
                                    key={video.key}
                                    className="flx a_center j_spc_btw"
                                >
                                    <div>{video.locationText ?? video.direction}</div>

                                    {isNormal ? (
                                        <div className="gr">정상</div>
                                    ) : (
                                        <div className="rd">장애</div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <div className={`${styles.accessCctvCell} ${styles.accessCctvBottomRight}`}>
                        <div className={styles.accessCctvPhotoFrame}>
                            <img
                                className={styles.accessCctvPhoto}
                                src={accessPhotoUrl}
                                alt={`${accessLocationName} 현장 사진`}
                                onError={handleAccessPhotoError}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </Zoom>
    );
};
